import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { getWebSiteSchema, getWebPageSchema, getBreadcrumbSchema } from '@/utils/schemas';

const categoryDetails: Record<string, {
  name: string;
  tagline: string;
  detail: string;
  features: string[];
  specifications: { label: string; value: string }[];
  image: string;
}> = {
  cuiguan: {
    name: '翠冠梨',
    tagline: '盛夏第一口清甜',
    detail: '翠冠梨产自惠美梨核心种植基地，地处北纬30°黄金水果种植带。这里气候温和、光照充足、昼夜温差大，赋予了翠冠梨独特的清甜口感。每一颗翠冠梨都经过严格筛选，果形端正、色泽均匀，单果重量在250g以上，含糖量达12%以上。从枝头到餐桌，全程冷链保鲜，确保每一口都是最新鲜的滋味。翠冠梨不仅适合直接鲜食，还可制作冰糖雪梨、梨膏等传统美食，润肺止咳，老少皆宜。',
    features: ['皮薄汁多', '细腻无渣', '清甜爽口', '夏季专属'],
    specifications: [
      { label: '单果重量', value: '250g-400g' },
      { label: '含糖量', value: '12%-15%' },
      { label: '成熟期', value: '7月中下旬' },
      { label: '保质期', value: '冷藏15天' },
      { label: '种植方式', value: '生态种植' },
      { label: '产地', value: '北纬30°黄金水果带' },
    ],
    image: 'https://readdy.ai/api/search-image?query=A%20single%20ripe%20greenish%20golden%20asian%20pear%20with%20a%20slice%20cut%20out%20revealing%20white%20juicy%20flesh%20on%20a%20minimal%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20food%20photography%2C%20dew%20drops%20on%20skin%2C%20clean%20composition%2C%20professional%20product%20photography&width=800&height=900&seq=cuiguan-detail&orientation=portrait',
  },
  huanghua: {
    name: '黄花梨',
    tagline: '金黄饱满，香甜浓郁',
    detail: '黄花梨在惠美梨自有果园中精心培育，采用生态种植方式，全程不使用化学催熟剂。果园采用智能化水肥管理系统，精准控制每一棵梨树的营养供给。成熟的黄花梨果皮金黄透亮，仿佛披上了秋日的暖阳。切开的瞬间，清甜的梨香扑鼻而来，汁水丰盈欲滴。黄花梨特别适合制作精美礼盒，惠美梨提供多种规格的礼盒包装，从6枚装到12枚装，满足不同送礼场景的需求。',
    features: ['金黄外观', '酥脆口感', '香甜浓郁', '送礼首选'],
    specifications: [
      { label: '单果重量', value: '300g-500g' },
      { label: '含糖量', value: '13%-16%' },
      { label: '成熟期', value: '8月下旬-9月上旬' },
      { label: '保质期', value: '冷藏20天' },
      { label: '种植方式', value: '生态种植' },
      { label: '产地', value: '自有果园基地' },
    ],
    image: 'https://readdy.ai/api/search-image?query=A%20single%20ripe%20golden%20yellow%20asian%20pear%20with%20a%20slice%20cut%20out%20revealing%20golden%20flesh%20on%20a%20minimal%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20food%20photography%2C%20warm%20golden%20tones%2C%20clean%20composition%2C%20professional%20product%20photography&width=800&height=900&seq=huanghua-detail&orientation=portrait',
  },
  fengshui: {
    name: '丰水梨',
    tagline: '汁水丰盈，润泽心田',
    detail: '惠美梨的丰水梨种植基地坐落于海拔800米以上的山地果园，充沛的降水和显著的昼夜温差造就了丰水梨独一无二的多汁特性。果园坚持有机种植标准，以农家肥为主、生物防治为辅，确保每一颗丰水梨都达到绿色食品标准。丰水梨富含多种维生素和矿物质，其中维生素C含量尤为突出，每100g果肉含维生素C约4mg，是日常补充维C的天然佳品。',
    features: ['汁多如泉', '肉质细嫩', '甜而不腻', '营养丰富'],
    specifications: [
      { label: '单果重量', value: '280g-450g' },
      { label: '含糖量', value: '11%-14%' },
      { label: '成熟期', value: '9月上旬-9月下旬' },
      { label: '保质期', value: '冷藏18天' },
      { label: '种植方式', value: '有机种植' },
      { label: '产地', value: '海拔800m山地果园' },
    ],
    image: 'https://readdy.ai/api/search-image?query=A%20single%20ripe%20brown%20skinned%20asian%20pear%20cut%20in%20half%20showing%20translucent%20juicy%20flesh%20on%20a%20minimal%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20food%20photography%2C%20droplets%20of%20juice%2C%20clean%20composition%2C%20professional%20product%20photography&width=800&height=900&seq=fengshui-detail&orientation=portrait',
  },
  xuehua: {
    name: '雪花梨',
    tagline: '洁白如雪，清雅甘甜',
    detail: '雪花梨是惠美梨传统优势品种，种植历史超过30年。果园位于天然水源保护区内，灌溉用水来自山泉溪流，水质纯净无污染。雪花梨的采摘时间在10月上旬，此时早晚温差已达15℃以上，极有利于糖分积累。雪花梨最大的特点是耐储存，在专业冷库中可保鲜至次年春季。惠美梨采用先进的气调贮藏技术，精准控制库内温度、湿度和气体成分，让雪花梨在长达半年的储存期内依然保持新鲜如初的口感和营养。',
    features: ['肉质洁白', '清雅甘甜', '耐储存', '养生佳品'],
    specifications: [
      { label: '单果重量', value: '200g-350g' },
      { label: '含糖量', value: '10%-13%' },
      { label: '成熟期', value: '10月上旬' },
      { label: '保质期', value: '冷藏180天' },
      { label: '种植方式', value: '传统生态种植' },
      { label: '产地', value: '天然水源保护区' },
    ],
    image: 'https://readdy.ai/api/search-image?query=A%20single%20ripe%20pale%20green%20asian%20pear%20cut%20in%20half%20showing%20snow%20white%20flesh%20on%20a%20minimal%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20food%20photography%2C%20clean%20crisp%20aesthetic%2C%20professional%20product%20photography&width=800&height=900&seq=xuehua-detail&orientation=portrait',
  },
};

export default function CategoryDetail() {
  const { id } = useParams<{ id: string }>();
  const category = id ? categoryDetails[id] : null;

  if (!category) {
    return (
      <>
        <SEOHead
          title="品种未找到 - 惠美梨"
          description="抱歉，您查找的梨果品种不存在。请浏览惠美梨其他品种。"
          canonicalUrl="https://hwsxsp.cn/category"
          schema={[getWebSiteSchema()]}
        />
        <Navbar />
        <main className="pt-16 md:pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">品种未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的梨果品种不存在。</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回品类页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `惠美梨${category.name} - ${category.tagline} | 惠美梨品种特色`;
  const pageDesc = `惠美梨${category.name}，${category.tagline}。${category.detail.substring(0, 100)}... 了解${category.name}详细规格、产地溯源与购买方式。`;

  const schemas = [
    getWebSiteSchema(),
    getWebPageSchema(`惠美梨${category.name}`, pageDesc, `https://hwsxsp.cn/category/${id}`),
    getBreadcrumbSchema([
      { name: '首页', url: 'https://hwsxsp.cn/' },
      { name: '品种品类', url: 'https://hwsxsp.cn/category' },
      { name: category.name, url: `https://hwsxsp.cn/category/${id}` },
    ]),
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        canonicalUrl={`https://hwsxsp.cn/category/${id}`}
        schema={schemas}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/category" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">品种品类</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 whitespace-nowrap">{category.name}</span>
            </nav>
          </div>
        </section>

        <section className="w-full pb-16 md:pb-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
              <div className="w-full lg:w-1/2">
                <div className="relative h-80 md:h-[500px] rounded-2xl overflow-hidden">
                  <img
                    src={category.image}
                    alt={`惠美梨${category.name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="w-full lg:w-1/2">
                <div className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  {category.tagline}
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
                  {category.name}
                </h1>

                <div className="flex flex-wrap gap-2 mb-6">
                  {category.features.map((f) => (
                    <span key={f} className="px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 text-sm font-medium">
                      {f}
                    </span>
                  ))}
                </div>

                <p className="text-foreground-600 leading-relaxed mb-8">
                  {category.detail}
                </p>

                <div className="bg-background-100 rounded-2xl p-6 mb-8">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                    产品规格
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {category.specifications.map((spec) => (
                      <div key={spec.label}>
                        <div className="text-xs text-foreground-400 mb-1">{spec.label}</div>
                        <div className="text-sm font-semibold text-foreground-900">{spec.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-medium text-sm hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回品种列表
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}